import { css } from 'lit';

import { narrow, reducedMotion } from '../media.js';

export const planeAppStyles = css`
  :host {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    min-height: 100dvh;
  }

  /* The sign above the door: black in both themes. */
  header {
    position: relative;
    padding-top: var(--safe-top);
    background: var(--masthead);
    color: var(--masthead-text);
  }

  /* The demo band above takes the status bar instead. */
  plane-demo-bar + header {
    padding-top: 0;
  }

  .header-inner {
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    gap: 0 var(--column-gap);
    max-width: var(--page-width);
    margin: 0 auto;
    padding: 0 calc(var(--page-gutter) + var(--safe-right)) 0 calc(var(--page-gutter) + var(--safe-left));
  }

  .brand {
    --mark-ink: var(--accent);
    display: inline-flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-4) 0;
    color: inherit;
    text-decoration: none;
  }

  .brand .mark {
    width: var(--brand-mark-size);
    height: var(--brand-mark-size);
    flex: none;
  }

  .wordmark {
    font-family: var(--font-display);
    font-weight: var(--font-display-weight);
    font-size: var(--font-size-xl);
    line-height: var(--line-height-none);
    letter-spacing: var(--font-display-tracking);
  }

  .wordmark small {
    display: block;
    margin-top: var(--space-1);
    font-family: var(--font-mono);
    font-size: var(--font-size-3xs);
    letter-spacing: var(--tracking-widest);
    text-transform: uppercase;
    color: var(--masthead-muted);
  }

  /* The pages are the lines of a network: each numbered, in the order of the navigation. */
  nav {
    display: flex;
    flex: 1;
    gap: 0;
    counter-reset: line;
    border-left: var(--border-width) solid var(--masthead-rule);
  }

  nav a,
  .account a {
    display: inline-flex;
    align-items: center;
    padding: 0 var(--space-3);
    color: var(--masthead-muted);
    text-decoration: none;
    font-size: var(--font-size-ui);
    font-weight: var(--font-weight-medium);
  }

  nav a {
    position: relative;
    gap: var(--space-2);
    padding-inline: var(--space-4);
    border-right: var(--border-width) solid var(--masthead-rule);
    counter-increment: line;
  }

  nav a::before {
    content: counter(line, decimal-leading-zero);
    font-family: var(--font-mono);
    font-size: var(--font-size-2xs);
    font-weight: var(--font-weight-normal);
    letter-spacing: var(--tracking-wide);
    color: var(--masthead-muted);
  }

  nav a:hover,
  .account a:hover {
    color: var(--masthead-text);
  }

  nav a[aria-current='page'] {
    color: var(--masthead-text);
    font-weight: var(--font-weight-bold);
  }

  nav a[aria-current='page']::before {
    color: var(--accent);
  }

  /* You are here: a red bar along the bottom of the current line. */
  nav a[aria-current='page']::after {
    content: '';
    position: absolute;
    inset: auto 0 0;
    height: var(--edge-width);
    background: var(--accent);
  }

  /* Something waits behind the link: a small count in the accent. */
  nav .pending {
    display: inline-grid;
    place-items: center;
    min-width: 1.25rem;
    height: 1.25rem;
    padding: 0 var(--space-1);
    border-radius: var(--radius);
    background: var(--accent);
    color: var(--accent-text);
    font-family: var(--font-mono);
    font-size: var(--font-size-2xs);
    font-weight: var(--font-weight-bold);
    line-height: var(--line-height-none);
  }

  /* Read by screen readers, not shown. */
  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  nav a:focus-visible,
  .account a:focus-visible,
  .brand:focus-visible {
    outline: var(--focus-ring);
    outline-offset: calc(var(--focus-ring-offset) * -2);
  }

  .account {
    display: flex;
    align-items: stretch;
    gap: var(--space-1);
    font-size: var(--font-size-ui);
  }

  .user {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding-right: var(--space-2);
    color: var(--masthead-text);
    font-family: var(--font-mono);
    font-size: var(--font-size-sm);
  }

  .user::before {
    content: '';
    width: var(--bullet-size);
    height: var(--bullet-size);
    background: var(--accent);
  }

  main {
    flex: 1;
    width: 100%;
    max-width: var(--page-width);
    margin: 0 auto;
    padding: var(--page-padding-block) calc(var(--page-gutter) + var(--safe-right))
      calc(var(--page-padding-block) * 2 + var(--bottom-overlay)) calc(var(--page-gutter) + var(--safe-left));
  }

  /* A page rises into place when it is opened. On the page element, which the router creates
     once per navigation: inside it, a poll may re-create whole subtrees, and they must not
     flicker. */
  main > * {
    animation: surface var(--duration-enter) var(--ease-out) both;
  }

  @keyframes surface {
    from {
      opacity: 0;
      transform: translateY(var(--enter-offset));
    }
  }

  @media ${reducedMotion} {
    main > * {
      animation: none;
    }
  }

  .expired {
    max-width: var(--form-width-sm);
    margin: calc(var(--page-padding-block) * 2) 0;
    padding-top: var(--space-4);
    border-top: var(--rule-width) solid var(--text);
  }

  .expired h1 {
    margin: 0 0 var(--space-3);
    font-family: var(--font-display);
    font-weight: var(--font-display-weight);
    font-size: var(--font-size-title);
    line-height: var(--line-height-tight);
    letter-spacing: var(--font-display-tracking);
  }

  .expired p {
    color: var(--text-muted);
    margin: 0 0 var(--space-5);
  }

  footer {
    border-top: var(--border-width) solid var(--border);
    color: var(--text-muted);
    font-size: var(--font-size-xs);
  }

  .footer-inner {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: var(--space-2) var(--space-5);
    max-width: var(--page-width);
    margin: 0 auto;
    padding: var(--space-4) calc(var(--page-gutter) + var(--safe-right)) calc(var(--space-4) + var(--safe-bottom))
      calc(var(--page-gutter) + var(--safe-left));
  }

  .footer-inner .colophon {
    font-family: var(--font-mono);
    letter-spacing: var(--tracking-wider);
    text-transform: uppercase;
  }

  @media ${narrow} {
    .header-inner {
      gap: 0 var(--space-3);
      padding-left: calc(var(--page-gutter-narrow) + var(--safe-left));
      padding-right: calc(var(--page-gutter-narrow) + var(--safe-right));
    }

    /* A row that scrolls when the pages do not fit: the right edge fades, so that a cut word
       reads as more to come, and the padding lets the last page scroll clear of the fade. */
    nav {
      order: 3;
      flex-basis: 100%;
      overflow-x: auto;
      border-top: var(--border-width) solid var(--masthead-rule);
      border-left: 0;
      margin-inline: calc(var(--space-3) * -1);
      padding-right: var(--space-6);
      scroll-padding-inline: var(--space-3) var(--space-6);
      scrollbar-width: none;
      mask-image: linear-gradient(to right, #000 calc(100% - var(--space-6)), transparent);
    }

    nav::-webkit-scrollbar {
      display: none;
    }

    nav a {
      padding: var(--space-3);
      border-right: 0;
    }

    .account {
      margin-left: auto;
    }

    main {
      padding-left: calc(var(--page-gutter-narrow) + var(--safe-left));
      padding-right: calc(var(--page-gutter-narrow) + var(--safe-right));
    }
  }
`;
